import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  VStack,
} from '@chakra-ui/react';
import { keyframes } from '@chakra-ui/system';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { useSupabaseTypedClient } from 'hooks/reactQuery';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Button from 'components/Button';
import Icon from 'components/Icon';
import Link from 'components/Link';
import { SEO } from 'components/SEO';
import Text from 'components/Text';
import {
  CompleteOnboardingStepParams,
  EdgeFunctionName,
  ManageEmailNotificationsParams,
  UpdateUserPrivateEmailParams,
  UpdateUserProfileParams,
} from 'types/edgeFunctions';
import { TanstackQueryName } from 'types/frontend';

interface OnboardingProfileForm {
  name: string;
  email: string;
}

const validationSchema = yup
  .object({
    name: yup
      .string()
      .required('Name is required')
      .max(255, 'Name is too long (maximum is 255 characters)'),
    email: yup
      .string()
      .required('Email is required')
      .email('Please enter a valid email address')
      .max(255, 'Email is too long (maximum is 255 characters)'),
  })
  .required();

const TERMS_URL = 'https://www.iubenda.com/terms-and-conditions/78614077';
const PRIVACY_URL = 'https://www.iubenda.com/privacy-policy/78614077';
const COOKIE_URL = 'https://www.iubenda.com/privacy-policy/78614077/cookie-policy';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const OnboardingProfileStep = () => {
  const supabase = useSupabaseTypedClient();
  const queryClient = useQueryClient();
  const { toastError } = useToastWrapper();
  const { name, privateEmail, id: userId } = useCurrentUser();

  const [subscribeToNewsletter, setSubscribeToNewsletter] = useState(false);
  const [subscribeToMarketing, setSubscribeToMarketing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<OnboardingProfileForm>({
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: name || '',
      email: privateEmail || '',
    },
  });

  const onSubmit = async (values: OnboardingProfileForm) => {
    setIsSubmitting(true);

    try {
      const trimmedName = values.name.trim();
      const trimmedEmail = values.email.trim();

      // 1. Update name if changed
      if (trimmedName !== name) {
        const { error } = await supabase.functions.invoke(EdgeFunctionName.UpdateUserProfile, {
          body: { name: trimmedName } as UpdateUserProfileParams,
        });
        if (error) throw error;
      }

      // 2. Update email if changed
      if (trimmedEmail !== privateEmail) {
        const { error } = await supabase.functions.invoke(EdgeFunctionName.UpdateUserPrivateEmail, {
          body: { email: trimmedEmail } as UpdateUserPrivateEmailParams,
        });
        if (error) throw error;
      }

      // 3. Subscribe to newsletter if opted in
      if (subscribeToNewsletter) {
        await supabase.functions.invoke(EdgeFunctionName.ManageEmailNotifications, {
          body: {
            shouldSubscribeToBiWeeklyNewsletter: true,
          } as ManageEmailNotificationsParams,
        });
      }

      // 4. Subscribe to marketing if opted in
      if (subscribeToMarketing) {
        await supabase.functions.invoke(EdgeFunctionName.ManageEmailNotifications, {
          body: {
            shouldSubscribeToMarketing: true,
          } as ManageEmailNotificationsParams,
        });
      }

      // 5. Move to personalize step
      const { error } = await supabase.functions.invoke(EdgeFunctionName.CompleteOnboardingStep, {
        body: { next_step: 'personalize' } as CompleteOnboardingStepParams,
      });

      if (error) throw error;

      // 6. Refresh user data so Routes re-evaluates
      await queryClient.invalidateQueries({
        queryKey: [TanstackQueryName.GetCurrentUser, userId],
      });
    } catch (error: any) {
      toastError(error?.message || 'Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="BigDevSoon | Complete Your Profile"
        description="Complete your profile to continue"
        url={`${process.env.REACT_APP_SITE_URL}onboarding`}
      />

      <VStack
        spacing={6}
        maxW="560px"
        width="100%"
        px={4}
        py={4}
      >
        {/* Header */}
        <VStack
          spacing={3}
          animation={`${fadeInUp} 0.5s ease-out`}
        >
          <HStack spacing={3} justify="center">
            <Icon type="Rocket" size={28} />
            <Text
              fontSize="xxxxLarge"
              fontWeight="bold"
              textAlign="center"
            >
              Welcome!
            </Text>
          </HStack>
          <Text fontSize="large" opacity="secondary" textAlign="center">
            {`Let's set up your profile to get started`}
          </Text>
        </VStack>

        {/* Form */}
        <Box
          as="form"
          onSubmit={handleSubmit(onSubmit)}
          width="100%"
          animation={`${fadeInUp} 0.7s ease-out`}
        >
          <Stack spacing={5}>
            <FormControl isInvalid={Boolean(errors.name)}>
              <FormLabel htmlFor="name" fontWeight="medium" fontSize="sm" mb={2}>
                Username
              </FormLabel>
              <InputGroup size="lg">
                <InputLeftElement pointerEvents="none">
                  <Icon type="Profile" />
                </InputLeftElement>
                <Input
                  {...register('name')}
                  variant="filled"
                  placeholder="Your username"
                  bg="whiteAlpha.50"
                  borderWidth={1}
                  borderColor="whiteAlpha.100"
                  _hover={{ borderColor: 'whiteAlpha.200', bg: 'whiteAlpha.100' }}
                  _focus={{
                    borderColor: 'purple.400',
                    bg: 'whiteAlpha.100',
                    boxShadow: '0 0 0 1px rgba(159,122,234,0.3)',
                  }}
                  borderRadius="xl"
                />
              </InputGroup>
              <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={Boolean(errors.email)}>
              <FormLabel htmlFor="email" fontWeight="medium" fontSize="sm" mb={2}>
                Email
              </FormLabel>
              <InputGroup size="lg">
                <InputLeftElement pointerEvents="none">
                  <Icon type="Email" />
                </InputLeftElement>
                <Input
                  {...register('email')}
                  variant="filled"
                  placeholder="your@email.com"
                  bg="whiteAlpha.50"
                  borderWidth={1}
                  borderColor="whiteAlpha.100"
                  _hover={{ borderColor: 'whiteAlpha.200', bg: 'whiteAlpha.100' }}
                  _focus={{
                    borderColor: 'purple.400',
                    bg: 'whiteAlpha.100',
                    boxShadow: '0 0 0 1px rgba(159,122,234,0.3)',
                  }}
                  borderRadius="xl"
                />
              </InputGroup>
              <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
              <Text fontSize="micro" opacity="tertiary" mt={2}>
                Private email for payments and communication. Not visible to others.
              </Text>
            </FormControl>

            <Divider borderColor="whiteAlpha.100" />

            <VStack spacing={4} align="start">
              <Text fontSize="small" fontWeight="semibold" opacity="secondary">
                Email preferences
              </Text>
              <Checkbox
                isChecked={subscribeToMarketing}
                onChange={(e) => setSubscribeToMarketing(e.target.checked)}
                spacing={3}
              >
                <Text fontSize="small" opacity="secondary">
                  Receive promotional offers & product updates
                </Text>
              </Checkbox>
              <Checkbox
                isChecked={subscribeToNewsletter}
                onChange={(e) => setSubscribeToNewsletter(e.target.checked)}
                spacing={3}
              >
                <Text fontSize="small" opacity="secondary">
                  Subscribe to LevelUp newsletter
                </Text>
              </Checkbox>
            </VStack>
          </Stack>

          <Button
            type="submit"
            isLoading={isSubmitting}
            width="100%"
            height="52px"
            fontSize="medium"
            fontWeight="semibold"
            mt={6}
            scheme="gradient"
            borderRadius="xl"
          >
            Continue
          </Button>
        </Box>

        <Text
          fontSize="micro"
          opacity="tertiary"
          textAlign="center"
        >
          By continuing, you agree to our{' '}
          <Link href={TERMS_URL}>Terms of Service</Link>,{' '}
          <Link href={PRIVACY_URL}>Privacy Policy</Link>, and{' '}
          <Link href={COOKIE_URL}>Cookie Policy</Link>.
        </Text>
      </VStack>
    </>
  );
};

export default OnboardingProfileStep;
